import { createTheme,  Theme, ThemeOptions } from '@mui/material/styles';
import { green, grey, yellow } from '@mui/material/colors';
import { DefaultTheme} from '@mui/styles';

/**
 * Extends the DefaultTheme interface with the properties and structure of the Theme interface.
 *
 * This allows the theme object provided by Material-UI to be used seamlessly
 * when applying custom styles using the `@mui/styles` package.
 *
 * The `@mui/styles` package enables the use of custom styling approaches with
 * Material-UI components. By extending the DefaultTheme, developers can leverage
 * the Material-UI `Theme` structure in their styled components or functions.
 *
 * Note: Ensure that the `Theme` interface from Material-UI is properly imported
 * before using this module declaration to avoid type mismatches.
 */
declare module '@mui/styles' {
    interface DefaultTheme extends Theme {}
}

/**
 * Extends the Material-UI Palette interface to include custom color definitions
 * for various application-specific use cases like task types, calendar highlights,
 * action responses, and borders.
 */
declare module "@mui/material/styles/createPalette" {
    interface Palette {
        taskColors: {
            projectTask: Palette['primary']
            internalTask: Palette['primary']
            absence: Palette['primary']
            sickness: Palette['primary']
            vacation: Palette['primary']
        }
        calColors: {
            today: Palette['primary']
            holiday: Palette['primary'];
        },
        actionResponses: {
            error: Palette['primary'],
            ok: Palette['primary'],
            warning: Palette['primary'],
        }
        border: Palette['primary']
    }

    interface PaletteOptions {
        taskColors: {
            projectTask: PaletteOptions['primary']
            internalTask: PaletteOptions['primary']
            absence: PaletteOptions['primary']
            sickness: PaletteOptions['primary']
            vacation: PaletteOptions['primary']
        }
        calColors: {
            today: PaletteOptions['primary']
            holiday: PaletteOptions['primary']
        },
        actionResponses: {
            error: PaletteOptions['primary'],
            ok: PaletteOptions['primary'],
            warning: PaletteOptions['primary'],
        },
        border: PaletteOptions['primary']
    }
}

let options: ThemeOptions;


export const DarkTheme: DefaultTheme = createTheme({

    components: {
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontSize: "1em"

                }
            }
        }
    },
    palette: {
        mode: 'dark',
        background: {
            default: '#192933',
        },
        primary: {
            light: '#C3CCD1',
            main: '#21445A',
            dark: '#192933',
            contrastText: '#fff'
        },
        secondary: {
            light: '#f0c8fe',
            main: '#45214A',
            dark: '#4B3B43',
            contrastText: '#fff'
        },
        taskColors: {
            projectTask: {main: "#3d502f"},
            internalTask: {main: "#928942"},
            absence: {main: "#521f56"},
            sickness: {main: "#4d3712"},
            vacation: {main: "#0c554e"}
        },
        calColors: {
            today: {main: "#F0F4D7"},
            holiday: {main: "#4c625b"}
        },
        actionResponses: {
            error: {main: '#E41111FF'},
            ok: {main: green[600]},
            warning: {main: yellow[300]}
        },
        border: {
            light: grey[200],
            main: grey[300],
            dark: grey[500],
        },
        error: {
            main: '#e41111'
        }

    },
    typography: {
        fontFamily: [
            'Arial',
            'Tahoma'
        ].join(',')
    },
    shape: {
        borderRadius: 5,
    },
    spacing: 5,
});



/**
 * Represents the main theme configuration for the application using Material-UI's `createTheme` function.
 *
 * The `MainTheme` variable defines a light mode theme with customized color palettes, typography, spacing, and component style overrides.
 *
 * Components:
 * - `MuiTooltip`: Overrides the tooltip style to set a font size of 1em.
 *
 * Palette:
 * - `mode`: Specifies the theme mode as 'light'.
 * - `primary`: Defines the primary color palette with light, main, dark, and contrastText variants.
 * - `secondary`: Defines the secondary color palette with light, main, and dark variants.
 * - `taskColors`: Custom colors for specific task types such as `projectTask`, `internalTask`, `absence`, `sickness`, and `vacation`.
 * - `calColors`: Custom colors for calendar-related properties like `today` and `holiday`.
 * - `actionResponses`: Predefined colors for various action response types like `error`, `ok`, and `warning`.
 * - `border`: Specifies light, main, and dark border colors.
 * - `error`: Customizes the error color.
 *
 * Typography:
 * - `fontFamily`: Sets the font stack to use Arial and Tahoma, separated by a comma.
 *
 * Shape:
 * - `borderRadius`: Defines the border radius as 5 pixels.
 *
 * Sp*/
export const MainTheme: DefaultTheme = createTheme({

    components: {
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontSize: "1em"

                }
            }
        }
    },
    palette: {
        /**
         *      primary: {
         *             light: '#577182',
         *             main: '#1D3646',
         *             dark: '#192933',
         *             contrastText: '#fff'
         *         },
         *         secondary: {
         *             light: '#CABAC2',
         *             main: '#45214A',
         *             dark: '#4B3B43',
         *             contrastText: '#fff'
         *         },
         *         taskColors: {
         *             projectTask: { main: "#BCF995" },
         *             internalTask:  { main: "#f8e145" },
         *             absence:  { main: "#f7d4fa"},
         *             sickness:  { main: "#f7e3c0"},
         *             vacation:  { main: "#bbede8"}
         *         },
         *         calColors: {
         *             today:  { main: "#F0F4D7"},
         *             holiday:  { main: "#B4EDDA"}
         *         },
         *         actionResponses: {
         *             error: { main: '#9e2b2b' },
         *             ok: { main: green[600] },
         *             warning: { main: yellow[300] }
         *         },
         *         border: {
         *             light: grey[200],
         *             main: grey[300],
         *             dark:  grey[500],
         *         },
         *         error: {
         *             main: '#9e2b2b'
         *         }
         */
        mode: 'light', // type: dark sets dark mode
        primary: {
            light: '#57abcd',
            main: '#2d96c1',
            dark: '#248FC2',
            contrastText: '#fff'
        },
        secondary: {
            light: '#fdf4e6',
            main: '#e1cb86',
            dark: '#9d8e5d'
        },
        taskColors: {
            projectTask: { main: "#BCF995" },
            internalTask:  { main: "#f8e145" },
            absence:  { main: "#f7d4fa"},
            sickness:  { main: "#f7e3c0"},
            vacation:  { main: "#bbede8"}
        },
        calColors: {
            today:  { main: "#F0F4D7"},
            holiday:  { main: "#B4EDDA"}
        },
        actionResponses: {
            error: { main: '#9e2b2b' },
            ok: { main: green[600] },
            warning: { main: yellow[300] }
        },
        border: {
            light: grey[200],
            main: grey[300],
            dark:  grey[500],
        },
        error: {
            main: '#9e2b2b'
        }
       /* hoverColor: {
            main: fade( "#000", 0.08 ),
        },
        buttonBorder: {
            main: fade('#000', 0.25)
        }*/
        //error uses default red
    },
    typography: {
        fontFamily: [ 
            'Arial',
            'Tahoma'
        ].join(',')
    },
    shape: {
        borderRadius: 5,
    },
    spacing: 5,
});

/**
 * Generates an additional color palette based on the provided theme.
 *
 * @param {Theme} theme - The theme object used to customize the color palette.
 * @returns {Object} An object containing two sets of colors: taskColors and calColors.
 *
 * taskColors:
 * - Contains colors related to various task types such as project task, internal task, absence, sickness, and vacation.
 *
 * calColors:
 * - Contains colors related to calendar elements such as today and holiday.
 */
export const AdditionalColorPalette = ( theme: Theme ) => ({
    taskColors: {
        projectTask: "#BCF995",
        internalTask: "#",
        absence: "#f7d4fa",
        sickness: "#f7e3c0",
        vacation: "#bbede8"
    },
    calColors: {
        today: "#F0F4D7",
        holiday: "#B4EDDA"
    }
});

export const MuiDataTableStyles = ( theme: Theme ) => ({
    header: {
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.common.white
    },
    cell: {
        backgroundColor: theme.palette.common.white
    }
});


/**
 * Styles configuration for form components, utilizing Material-UI's theme properties.
 * This function returns a set of CSS-in-JS style rules tailored to a Material-UI theme.
 *
 * @param {Theme} theme - The Material-UI theme object used to customize the styling.
 * @returns {Object} An object containing style rules for various form-related elements.
 *
 * Properties:
 * - formRoot: Styles for the root container of the form, including responsive padding for small screens.
 * - select: Styles for a select input element with adjusted width and bottom margin.
 * - container: Styles for a container element with full width and automatic horizontal centering.
 * - input: Styles for an input field, including fixed width, border radius, and bottom margin.
 * - highlightHelper: Styles for highlighting text, using the primary color defined in the theme.
 * - switchGridRoot: Styles for the root of a grid containing a switch, applying left padding.
 * - datePicker: Styles for a date picker component, with fixed width and auto height.
 * - activeDisabledSwitchKnob: Overrides the color of a switch knob in its active-disabled state using a secondary dark theme color.
 * - activeDisabledSwitchTrack: Overrides the background color of a switch track in its active-disabled state using a secondary dark theme color.
 */
export const FormStyles = ( theme: Theme ) => ({
    formRoot: {
        width: "95%",
        margin: "auto",
        marginBottom: 30+'px',
        marginTop: 5+'px',
        [ theme.breakpoints.up('sm') ]: {
            padding: "10px"
        }
    },
    select: {
        width: 99+'%',
        marginBottom: 10+'px',
    },
    container: {
        width: 99+'%',
        marginBottom: 10+'px',
        margin: 'auto'
    },
    input: {
        width: "99%",
        borderRadius: 0,
        marginBottom: 10+"px"
    },
    highlightHelper: {
        color: theme.palette.mode === 'dark'
            ? theme.palette.secondary.light
            : theme.palette.primary.main
    },
    switchGridRoot: {
        paddingLeft: 15+'px'
    },
    datePicker: {
        height: 'auto',
        width: 400+'px'
    },
    activeDisabledSwitchKnob: {
        color: theme.palette.secondary.dark + ' !important',
    },
    activeDisabledSwitchTrack: {
        backgroundColor: theme.palette.secondary.dark + ' !important'
    }
});

/**
 * A function that generates style definitions for a stepper form component.
 * Applies different styles for desktop and mobile responsiveness, as well as
 * various states of the stepper elements such as active and hover states.
 *
 * @param {Theme} theme - The theme object used to define contextual design properties.
 * @returns {Object} An object containing the styles for the stepper form component.
 *
 * Properties:
 * - `root`: Defines the base width of the component.
 * - `step`: Styles for the individual step items, including hover effects.
 * - `activeStepLabel`: Styles for the active step label, including padding, color,
 *   font-weight, and background color based on the theme's secondary palette.
 * - `stepperDesktop`: Displays the stepper for desktop sizes. Hidden*/
export const StepperFormStyles = (theme: Theme) => ({
    ...buttonStyles(theme),
    root: {
        width: "98%"
    },
    step: {
        '&:hover': {
            cursor: 'pointer',
        }
    },
    stepContent: {
        width: "100%"
    },
    activeStepLabel: {
    //    borderRight: "5px solid #1f6987",
        padding: 5+'px',
        color: theme.palette.mode === 'dark'
            ? theme.palette.secondary.light + " !important"
            : theme.palette.secondary.contrastText + " !important",
        //@ts-ignore
        fontWeight: 'bold !important',
        backgroundColor: theme.palette.mode === 'dark'
            ? theme.palette.background.default
            : theme.palette.secondary.light
    },
    stepperDesktop: {
        display: 'none',
        [ theme.breakpoints.up('sm') ]: {
            display: 'flex'
        }
    },
    stepperMobile: {
        display: 'flex',
        [theme.breakpoints.up('sm')]: {
            display: 'none',
        },
    }

});

/**
 * Generates styling rules for a drag handle component to ensure consistent design and usability.
 *
 * This function creates and returns a set of styles for a drag handle element.
 * The styles include default color settings and hover-state behavior, ensuring the component
 * adheres to the visual guidelines defined in the provided theme.
 *
 * @param theme The theme object that provides design tokens such as colors and other UI constants.
 *              The theme must include `palette.secondary.dark` and `palette.action.hover` properties
 *              to support the styling definitions.
 * @return An object containing the drag handle's CSS styles, including default color and hover state rules.
 */
export function dragHandleStyles(theme: Theme) {
    return {
        dragHandle: {
            color: theme.palette.mode === 'dark'
                ? theme.palette.secondary.light
                : theme.palette.secondary.dark,
            '&:hover': {
                backgroundColor: theme.palette.action.hover,
                cursor: 'grabbing'
            },
        },
    };
}

/**
 * Generates a set of styles for displaying user avatars in a consistent and visually appealing way.
 *
 * This function provides styles for user avatars sized for standard and small displays. It ensures avatars
 * adhere to the theme's primary color scheme and contrast settings, while maintaining a circular shape and
 * centered content.
 *
 * @param theme - The design theme object used to retrieve palette colors and styling details. This is typically
 *                provided by the application's theme provider.
 * @return An object containing two style definitions:
 *         - `userAvatar`: Styles for the regular-sized user avatar.
 *         - `userAvatarSmall`: Styles for the small-sized user avatar.
 * @throws Will throw an error if the provided `theme` object is null, undefined, or does not contain
 *         the required `palette` structure with `primary` and `contrastText` values.
 */
export function userAvatarStyles(theme: Theme) {

    const shared = {
        borderRadius: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    }
    const colors = theme.palette.mode === 'dark'
        ? {
            backgroundColor: "#CABAC2",
            color: theme.palette.common.black,
            border: "1px solid "+ theme.palette.secondary.light,
        }
        : {
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.contrastText,
            border: "1px solid "+ theme.palette.primary.contrastText,
        }

    return {
        userAvatar: {
            ...colors,
            ...shared,
            width: 60,
            height: 60,
            fontSize: 20,
        },
        userAvatarMedium: {
            ...colors,
            ...shared,
            width: 35,
            fontWeight: 550,
            height: 35,
            fontSize: 15,
        },
        userAvatarSmall: {
            ...colors,
            ...shared,
            width: 25,
            height: 25,
            fontSize: 12,
        }
    };
}

/**
 * Generates styles for divider components based on the provided theme.
 *
 * @param {Theme} theme - The theme object used to determine the styles.
 * @returns {Object} An object containing style definitions for dividers.
 *    - dividerBlue: Styles for a blue divider, including width and background color.
 */
export const dividerStyles = (theme: Theme) => ({
    dividerBlue: {
        width: '100%',
        backgroundColor: theme.palette.primary.light
    },
 });


/**
 * Generates styles for different button variants based on the provided theme.
 *
 * @param {Theme} theme - The theme object containing palette and spacing information.
 * @return {Object} An object containing styles for various button variants, including:
 *   - `buttonActive`: Style for an active button with minimal borders and a white background.
 *   - `button`: Default button style with black border color and a white background.
 *   - `buttonVariantSecondary`: Style for a secondary button variant with elevated font weight and primary color highlights.
 *   - `buttonVariantSecondaryActive`: Style for an active secondary button variant with primary main color for background and white text.
 *   - `buttonVariantSecondaryDisabled`: Style for a disabled secondary button variant with greyed-out colors.
 *   - `buttonVariantTertiary`: Style for a tertiary button variant with teal shades and hover effects.
 *   - `buttonVariantTertiaryActive`: Style for an active tertiary button variant with hover inversion.
 *   - `buttonVariantTertiaryDisabled`: Style for a disabled tertiary button variant with greyed-out colors.
 *   - `importantButton`: Highlighted button style with primary main color and white text.
 *   - `disabledButton`: Style for a disabled button with muted text and background colors.
 *   - `activeButton`: Style for an active button with black text and a light secondary background.
 *   - `dangerousButton`: Style for a button indicating danger, with a red background and white text.
 *   - `tableButtonStyle`: Style for a button used in tables, with primary main color and hover effects.
 *   - `tableButtonStyleActive`: Style for an active table button with secondary dark color and highlighted background.
 */
export function buttonStyles( theme: Theme ) {
    const sharedProps = {
        lineHeight: 1.25+'em' +' !important',
        marginLeft: 5+'px' +' !important',
        height: '40px',
        padding: 5+'px',
        display: "flex",
        alignItems: "center",
        fontWeight: "500",
    };

    return {
        buttonActive: {
            ...sharedProps,
            borderTop: "0 !important",
            borderLeft: "0 !important",
            borderBottom: "0 !important",
            borderRight: "1px solid black !important",
            borderRadius: 0 + "!important",
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.common.white,
            '&:hover': {
                color: theme.palette.secondary.main
            },
        },
        button: {
            ...sharedProps,
            borderTop: "0 !important",
            borderLeft: "0 !important",
            borderBottom: "0 !important",
            borderRight: "1px solid "+ theme.palette.secondary.contrastText,
            color: theme.palette.secondary.contrastText,
            borderRadius: 0 + "!important",
            '&:hover': {
                color: theme.palette.secondary.dark,
                backgroundColor: theme.palette.common.white
            }
        // height: 1.25+'em',
        },
        buttonVariantSecondary: {
            ...sharedProps,
            fontWeight: '600',
            border: 0 + " !important",
            borderRadius: 8+"px",
            backgroundColor: "rgb(233,244,249) !important",
            color: theme.palette.primary.dark + " !important",
            '&:hover': {
                backgroundColor: theme.palette.primary.dark + " !important",
                color: theme.palette.primary.contrastText + " !important",
                border: "1px solid "+theme.palette.primary.contrastText + " !important",
            }
        },
        buttonVariantSecondaryActive: {
            ...sharedProps,
            borderRadius: 8+"px",
            fontWeight: '600',
            backgroundColor: theme.palette.primary.main,
            border: "1px solid "+theme.palette.primary.contrastText + " !important",
            color: theme.palette.common.white,
            '&:hover': {
                backgroundColor: theme.palette.primary.main,
            }
        },
        buttonVariantSecondaryDisabled: {
            ...sharedProps,
            borderRadius: 8+"px",
            border: 0 + " !important",
            fontWeight: '600',
            backgroundColor: 'grey',
            color: 'darkgrey',
        },
        buttonVariantTertiary: {
            ...sharedProps,
            fontWeight: '600',
            border: 0 + " !important",
            borderRadius: 8+"px",
            backgroundColor: "#E8EDEF !important",
            color: "#1C4E63 !important",
            '&:hover': {
                backgroundColor: "#1C4E63 !important",
                color: "#E8EDEF !important",
            }
        },
        buttonVariantTertiaryActive: {
            ...sharedProps,
            borderRadius: 8+"px",
            border: 0 + " !important",
            fontWeight: '600',
            backgroundColor: "#1C4E63 !important",
            color: "#E8EDEF !important",
            '&:hover': {
                backgroundColor: "#E8EDEF !important",
                color: "#1C4E63 !important",
            }
        },
        buttonVariantTertiaryDisabled: {
            ...sharedProps,
            borderRadius: 8+"px",
            border: 0 + " !important",
            fontWeight: '600',
            backgroundColor: 'grey',
            color: 'darkgrey',
        },
        importantButton: {
            ...sharedProps,
            color: theme.palette.common.white +' !important',
            backgroundColor: theme.palette.primary.main +' !important',
            '&:hover': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.common.white
            },
        },
        disabledButton: {
            ...sharedProps,
            color: 'rgba( 0,0,0, 0.20 ) !important',
            backgroundColor: 'rgba( 0,0,0, 0.08 ) !important',

        },
        activeButton: {
            ...sharedProps,
            color: theme.palette.common.black +' !important',
            backgroundColor: theme.palette.secondary.light +' !important',
        },
        dangerousButton: {
            ...sharedProps,
            color: theme.palette.common.white +' !important',
            backgroundColor: theme.palette.mode === 'dark'
                ? "#CD4141"
                :'#9e2b2b' +' !important',
        // height: 1.25+'em',
            '&:hover': {
                backgroundColor: 'rgba( 255,255,255, 0.08 )'+' !important',
                color: ( theme.palette.mode === 'dark' ? '#FF9595' : '#9e2b2b')+' !important'
            },
        },

        tableButtonStyle: {
            color: theme.palette.mode === 'dark'
                ? theme.palette.secondary.light +" !important"
                : theme.palette.primary.main +" !important",
            padding: theme.spacing(),
            '&:hover': {
                color: theme.palette.mode === 'dark'
                    ? "white !important"
                    : 'black !important'
            }
        },
        tableButtonStyleActive: {
            color: theme.palette.secondary.dark +" !important",
            backgroundColor: "#C8E0F4FF !important",
            padding: theme.spacing(),
            '&:hover': {
                color: theme.palette.primary.main +" !important",
            }
        }
    }
}

/**
 * returns the style class name according to the given shouldWarn function
 *
 * shouldWarn returns:
 *      - true -> bg-orange
 *      - false -> bg-green
 *      - null -> empty string
 */
export function healthIndicator( shouldWarn: () => boolean | null ) {
    if( shouldWarn() === null ) return " ";

    return shouldWarn()
        ? " bg-orange "
        : " bg-green ";

}

/**
 * Function to define styles for an expandable panel component.
 *
 * @param {Theme} theme - The theme object used to access palette and styling properties.
 * @returns {Object} An object containing styles for the expandable panel component.
 *
 * - `expandableFormActiveContainer`: Styles applied to the active container of the expandable form.
 *   - `backgroundColor`: Sets the background color to the secondary main color defined in the theme palette.
 *
 * - `expandableContainer`: Styles applied to the main expandable container.
 *   - `backgroundColor`: Sets the background color to the common white color defined in the theme palette.
 */
export const expandablePanelStyles = ( theme: Theme ) => ({
    expandableFormActiveContainer: {
        backgroundColor: theme.palette.secondary.main
    },
    expandableContainer: {
        backgroundColor: theme.palette.common.white,
    }
});